// src/pages/pmc/PmcLoaWorkOrderPane.js

import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage, auth } from "../../firebaseConfig";
import toast from "react-hot-toast";
import generateLOAPDF from "../../utils/generateLOAPDF";

const formatINR = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const clean = (v = "") => String(v || "").trim().toLowerCase();

function getState(x = {}) {
  return x.state || x.stateName || x.projectState || x.pacsState || "";
}

function getDistrict(x = {}) {
  return (
    x.district ||
    x.districtName ||
    x.projectDistrict ||
    x.pacsDistrict ||
    x.city ||
    ""
  );
}

function isStrictNafedProject(x = {}) {
  return (
    x.nafedProject === true &&
    x.projectSource === "NAFED" &&
    x.applicationPortal === "NAFED_PACS_APPLY"
  );
}

function applyNafedScopeToProject(project, nafedScope) {
  if (!nafedScope?.onlyNafedApply && !nafedScope?.strictNafedApplyOnly) {
    return true;
  }

  const scopeState = clean(nafedScope?.state);
  const allowedDistricts = (
    nafedScope?.allowedDistricts ||
    nafedScope?.districts ||
    []
  ).map(clean);

  const stateOk = !scopeState || clean(getState(project)) === scopeState;
  const districtOk =
    !allowedDistricts.length ||
    allowedDistricts.includes(clean(getDistrict(project)));

  return isStrictNafedProject(project) && stateOk && districtOk;
}

function tenderMatchesAssignedProject(tender, assignedProjectIds, assignedApplicationIds) {
  const possibleProjectIds = [
    tender.projectId,
    tender.workflowProjectId,
    tender.wfProjectId,
    tender.godownProjectId,
    tender.godownApplicationDocId,
    tender.applicationDocId,
    tender.linkedProjectId,
    tender.linkedGodownProjectId,
  ].filter(Boolean);

  const possibleApplicationIds = [
    tender.applicationId,
    tender.godownApplicationId,
    tender.linkedApplicationId,
    tender.pacsApplicationId,
    tender.projectApplicationId,
  ].filter(Boolean);

  return (
    possibleProjectIds.some((id) => assignedProjectIds.has(String(id))) ||
    possibleApplicationIds.some((id) => assignedApplicationIds.has(String(id)))
  );
}

function Badge({ status }) {
  const s = String(status || "").toUpperCase();

  let type = "pending";
  if (s.includes("ACCEPTED") || s.includes("APPROVED")) type = "accepted";
  if (s.includes("REJECT")) type = "rejected";
  if (s.includes("SIGNED") || s.includes("GENERATED") || s.includes("UPLOADED")) {
    type = "generated";
  }

  return <span className={`loa-badge ${type}`}>{status || "PENDING"}</span>;
}

function StatCard({ label, value, note }) {
  return (
    <div className="loa-stat-card">
      <label>{label}</label>
      <b>{value}</b>
      {note && <small>{note}</small>}
    </div>
  );
}

export default function PmcLoaWorkOrderPane({ nafedScope }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [uploadingId, setUploadingId] = useState("");
  const [generatingId, setGeneratingId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [remarksMap, setRemarksMap] = useState({});
  const [generateDateMap, setGenerateDateMap] = useState({});
  const [durationMap, setDurationMap] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const isNafedMode =
    nafedScope?.onlyNafedApply || nafedScope?.strictNafedApplyOnly;

  const getCountdown = (row) => {
    if (!row.workStartDate || !row.workDurationDays) {
      return { daysLeft: "—", label: "Not Set", color: "muted" };
    }

    const start = new Date(row.workStartDate);
    const today = new Date();

    start.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const passed = Math.floor((today - start) / (1000 * 60 * 60 * 24));
    const left = Number(row.workDurationDays) - passed;

    if (left < 0) {
      return { daysLeft: Math.abs(left), label: "Overdue", color: "red" };
    }

    if (left <= 7) {
      return { daysLeft: left, label: "Urgent", color: "amber" };
    }

    return { daysLeft: left, label: "On Time", color: "green" };
  };

  const getAssignedProjects = async () => {
    const snap = await getDocs(collection(db, "godown_projects"));

    const assigned = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((p) => applyNafedScopeToProject(p, nafedScope));

    const projectIds = new Set(assigned.map((p) => String(p.id)));

    const applicationIds = new Set(
      assigned.map((p) => String(p.applicationId || "")).filter(Boolean)
    );

    return { assigned, projectIds, applicationIds };
  };

  const loadRows = async () => {
    try {
      setLoading(true);

      const tenderSnap = await getDocs(collection(db, "tenders"));
      const godownSnap = await getDocs(collection(db, "godown_projects"));

      const godownList = godownSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      let assignedProjectIds = new Set();
      let assignedApplicationIds = new Set();

      if (isNafedMode) {
        const scope = await getAssignedProjects();
        assignedProjectIds = scope.projectIds;
        assignedApplicationIds = scope.applicationIds;
      }

      let list = tenderSnap.docs
        .map((d) => {
          const t = { id: d.id, ...d.data() };

          const linkedGodown =
            godownList.find(
              (g) =>
                g.id === t.projectId ||
                g.id === t.godownProjectId ||
                g.id === t.workflowProjectId ||
                g.applicationId === t.applicationId ||
                g.applicationId === t.godownApplicationId ||
                g.applicationId === t.projectApplicationId
            ) ||
            godownList.find(
              (g) =>
                clean(g.pacsName) ===
                clean(t.ownerCompany || t.pacsName || t.organization || "")
            );

          return {
            ...t,
            linkedGodownId: linkedGodown?.id || "",
            nafedProject: t.nafedProject === true || linkedGodown?.nafedProject === true,
            projectSource: t.projectSource || linkedGodown?.projectSource || "",
            applicationPortal:
              t.applicationPortal || linkedGodown?.applicationPortal || "",
            state: t.state || linkedGodown?.state || "Gujarat",
            district: t.city || t.district || linkedGodown?.district || "—",
            taluka: t.taluka || linkedGodown?.taluka || "",
            village: t.village || linkedGodown?.village || "",
            capacityMt:
              t.capacityMt ||
              t.godownCapacity ||
              linkedGodown?.capacityMt ||
              linkedGodown?.godownCapacity ||
              "",
          };
        })
        .filter((t) => t.awardedVendorUid);

      if (isNafedMode) {
        const scopeState = clean(nafedScope?.state);
        const allowedDistricts = (
          nafedScope?.allowedDistricts ||
          nafedScope?.districts ||
          []
        ).map(clean);

        list = list.filter((t) => {
          const linked = tenderMatchesAssignedProject(
            t,
            assignedProjectIds,
            assignedApplicationIds
          );

          const strictTender = t.nafedProject === true && t.projectSource === "NAFED";
          const stateOk = !scopeState || clean(getState(t)) === scopeState;
          const districtOk =
            !allowedDistricts.length ||
            allowedDistricts.includes(clean(getDistrict(t)));

          return linked || (strictTender && stateOk && districtOk);
        });
      }

      const formatted = list.map((t) => ({
        id: t.id,
        ...t,
        projectTitle: t.title || t.projectName || t.workName || "Project",
        pacsName: t.ownerCompany || t.pacsName || t.organization || "—",
        contractorName: t.awardedVendorName || t.vendorName || "—",
        contractorUid: t.awardedVendorUid || "—",
        awardAmount:
          t.awardedAmount || t.awardedValue || t.l1Amount || t.amount || 0,
        loaPdfUrl: t.loaPdfUrl || t.workOrderPdfUrl || "",
        signedLoaUrl: t.signedLoaUrl || "",
        loaStatus: t.loaStatus || "PENDING",
        loaPmcStatus: t.loaPmcStatus || "PENDING",
        loaPmcRemarks: t.loaPmcRemarks || "",
        loaGenerateDate: t.loaGenerateDate || "",
        workStartDate: t.workStartDate || "",
        workDurationDays: t.workDurationDays || 0,
      }));

      formatted.sort(
        (a, b) =>
          (b.awardedAt?.seconds || b.createdAt?.seconds || 0) -
          (a.awardedAt?.seconds || a.createdAt?.seconds || 0)
      );

      setRows(formatted);
    } catch (e) {
      console.error(e);
      toast.error("LOA register load failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, [JSON.stringify(nafedScope || {})]);

  const generateAutoLOA = async (row) => {
    try {
      setGeneratingId(row.id);

      const today = new Date().toISOString().slice(0, 10);

      const loaGenerateDate =
        generateDateMap[row.id] || row.loaGenerateDate || today;

      const workDurationDays = Number(
        durationMap[row.id] || row.workDurationDays || 180
      );

      const l1Amount = Number(row.awardAmount || 0);
      const gstRate = Number(row.gstRate || 18);
      const gstAmount = Math.round((l1Amount * gstRate) / 100);
      const totalAmount = l1Amount + gstAmount;

      const loaNo = `SAAM/NAFED/LOA/${new Date().getFullYear()}/${row.id
        .slice(0, 6)
        .toUpperCase()}`;

      const loaData = {
        loaNo,
        loaDate: loaGenerateDate,
        loaGenerateDate,
        workStartDate: loaGenerateDate,
        workDurationDays,

        tenderId: row.id,
        projectTitle: row.projectTitle,
        pacsName: row.pacsName,
        projectCapacity: row.capacityMt
          ? `${row.capacityMt} MT`
          : row.projectCapacity || "1000 MT",

        district: row.district,
        taluka: row.taluka || "",
        state: row.state || "Gujarat",
        projectLocation: row.projectLocation || row.village || row.siteLocation || "",

        contractorName: row.contractorName,
        contractorAddress:
          row.awardedVendorAddress ||
          row.contractorAddress ||
          row.vendorAddress ||
          "",

        contractorUid: row.contractorUid,

        l1Amount,
        gstRate,
        gstAmount,
        totalAmount,

        performanceSecurity: 250000,
        paymentReleaseDays: 10,
        raBillMinimum: 525000,
        jurisdiction: "Palanpur, District Banaskantha, Gujarat",

        boqPdfUrl:
          row.boqPdfUrl ||
          row.boqUrl ||
          row.boqFileUrl ||
          row.tenderBoqUrl ||
          "",
      };

      const pdfBlob = await generateLOAPDF(loaData);

      const fileName = `${loaNo.replaceAll("/", "-")}.pdf`;
      const path = `generated_loas/${row.id}/${Date.now()}_${fileName}`;

      const sRef = ref(storage, path);
      await uploadBytes(sRef, pdfBlob);

      const url = await getDownloadURL(sRef);

      await updateDoc(doc(db, "tenders", row.id), {
        loaNo,
        loaDate: loaGenerateDate,
        loaGenerateDate,
        workStartDate: loaGenerateDate,
        workDurationDays,
        loaPdfUrl: url,
        loaPdfPath: path,
        loaPdfName: fileName,
        loaStatus: "LOA_GENERATED",
        loaGeneratedAt: serverTimestamp(),
        loaGeneratedBy: auth.currentUser?.uid || "",
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, "loa_register"), {
        tenderId: row.id,
        loaNo,
        loaDate: loaGenerateDate,
        pacsName: row.pacsName,
        contractorName: row.contractorName,
        awardAmount: l1Amount,
        state: row.state || "",
        district: row.district || "",
        projectCapacity: row.capacityMt ? `${row.capacityMt} MT` : "",
        loaPdfUrl: url,
        loaPdfPath: path,
        generatedBy: auth.currentUser?.uid || "",
        generatedAt: serverTimestamp(),
      });

      toast.success("LOA auto generated successfully.");
      await loadRows();
    } catch (e) {
      console.error(e);
      toast.error("Auto LOA generate failed.");
    } finally {
      setGeneratingId("");
    }
  };

  const uploadLoaPdf = async (row, file) => {
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Please upload PDF file only.");
      return;
    }

    try {
      setUploadingId(row.id);

      const safeName = file.name.replace(/\s+/g, "_");
      const path = `loa_work_orders/${row.id}/${Date.now()}_${safeName}`;

      const sRef = ref(storage, path);
      await uploadBytes(sRef, file);

      const url = await getDownloadURL(sRef);

      const loaGenerateDate =
        generateDateMap[row.id] ||
        row.loaGenerateDate ||
        new Date().toISOString().slice(0, 10);

      const workDurationDays = Number(
        durationMap[row.id] || row.workDurationDays || 0
      );

      await updateDoc(doc(db, "tenders", row.id), {
        loaPdfUrl: url,
        loaPdfPath: path,
        loaPdfName: file.name,
        loaGenerateDate,
        workStartDate: loaGenerateDate,
        workDurationDays,
        loaStatus: "LOA_UPLOADED",
        loaUploadedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast.success("LOA PDF uploaded successfully.");
      await loadRows();
    } catch (e) {
      console.error(e);
      toast.error("LOA upload failed.");
    } finally {
      setUploadingId("");
    }
  };

  const updateWorkSchedule = async (row) => {
    try {
      setEditingId(row.id);

      const loaGenerateDate =
        generateDateMap[row.id] || row.loaGenerateDate || row.workStartDate || "";

      const workDurationDays = Number(
        durationMap[row.id] || row.workDurationDays || 0
      );

      await updateDoc(doc(db, "tenders", row.id), {
        loaGenerateDate,
        workStartDate: loaGenerateDate,
        workDurationDays,
        updatedAt: serverTimestamp(),
      });

      toast.success("Work schedule updated.");
      await loadRows();
    } catch (e) {
      console.error(e);
      toast.error("Schedule update failed.");
    } finally {
      setEditingId("");
    }
  };

  const setDecision = async (row, decision) => {
    if (!row.signedLoaUrl) {
      toast.error("Signed LOA not uploaded by contractor yet.");
      return;
    }

    try {
      setSavingId(row.id);

      const remarks = remarksMap[row.id] ?? row.loaPmcRemarks ?? "";

      await updateDoc(doc(db, "tenders", row.id), {
        loaPmcStatus: decision,
        loaStatus:
          decision === "ACCEPTED"
            ? "SIGNED_ACCEPTED"
            : decision === "REJECTED"
            ? "SIGNED_REJECTED"
            : row.loaStatus || "PENDING",
        loaPmcRemarks: remarks,
        loaDecisionAt: serverTimestamp(),
        loaDecisionBy: auth.currentUser?.uid || "",
        updatedAt: serverTimestamp(),
      });

      toast.success(`LOA ${decision.toLowerCase()} successfully.`);
      await loadRows();
    } catch (e) {
      console.error(e);
      toast.error("LOA status update failed.");
    } finally {
      setSavingId("");
    }
  };

  const summary = useMemo(() => {
    const total = rows.length;
    const loaAvailable = rows.filter((x) => x.loaPdfUrl).length;
    const signedSubmitted = rows.filter((x) => x.signedLoaUrl).length;
    const accepted = rows.filter(
      (x) => String(x.loaPmcStatus || "").toUpperCase() === "ACCEPTED"
    ).length;
    const rejected = rows.filter(
      (x) => String(x.loaPmcStatus || "").toUpperCase() === "REJECTED"
    ).length;
    const pending = rows.filter(
      (x) =>
        x.signedLoaUrl &&
        String(x.loaPmcStatus || "PENDING").toUpperCase() === "PENDING"
    ).length;

    return { total, loaAvailable, signedSubmitted, accepted, rejected, pending };
  }, [rows]);

  const filteredRows = useMemo(() => {
    let list = [...rows];

    if (statusFilter === "loaMissing") list = list.filter((x) => !x.loaPdfUrl);
    if (statusFilter === "signedPending") list = list.filter((x) => !x.signedLoaUrl);

    if (statusFilter === "reviewPending") {
      list = list.filter(
        (x) =>
          x.signedLoaUrl &&
          String(x.loaPmcStatus || "PENDING").toUpperCase() === "PENDING"
      );
    }

    if (statusFilter === "accepted") {
      list = list.filter(
        (x) => String(x.loaPmcStatus || "").toUpperCase() === "ACCEPTED"
      );
    }

    if (statusFilter === "rejected") {
      list = list.filter(
        (x) => String(x.loaPmcStatus || "").toUpperCase() === "REJECTED"
      );
    }

    const q = search.trim().toLowerCase();

    if (q) {
      list = list.filter((x) =>
        [
          x.projectTitle,
          x.pacsName,
          x.district,
          x.state,
          x.contractorName,
          x.contractorUid,
          x.id,
        ]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    return list;
  }, [rows, search, statusFilter]);

  return (
    <div className="loa-page">
      <style>{`
        .loa-page {
          display: grid;
          gap: 18px;
          font-family: Inter, "Segoe UI", Arial, sans-serif;
        }

        .loa-hero {
          background: linear-gradient(135deg, #062b55, #064e3b);
          color: white;
          border-radius: 22px;
          padding: 24px;
          box-shadow: 0 18px 40px rgba(6,43,85,.20);
        }

        .loa-hero small {
          color: #bbf7d0;
          font-size: 11px;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .18em;
        }

        .loa-hero h2 {
          margin: 6px 0 0;
          font-size: 26px;
          font-weight: 950;
        }

        .loa-hero p {
          margin: 8px 0 0;
          color: #d1fae5;
          font-size: 13px;
          line-height: 1.7;
          max-width: 900px;
        }

        .loa-stats {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 12px;
        }

        .loa-stat-card {
          background: white;
          border: 1px solid #dbe4ef;
          border-radius: 16px;
          padding: 16px;
          box-shadow: 0 8px 22px rgba(15,23,42,.05);
        }

        .loa-stat-card label {
          display: block;
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .12em;
        }

        .loa-stat-card b {
          display: block;
          margin-top: 8px;
          color: #071f45;
          font-size: 24px;
          font-weight: 950;
        }

        .loa-stat-card small {
          display: block;
          margin-top: 5px;
          color: #64748b;
          font-size: 11px;
          font-weight: 750;
        }

        .loa-filter {
          background: white;
          border: 1.5px solid #111827;
          border-radius: 18px;
          padding: 16px;
          display: grid;
          grid-template-columns: 260px 1fr;
          gap: 12px;
          box-shadow: 0 8px 22px rgba(15,23,42,.06);
        }

        .loa-field {
          display: grid;
          gap: 6px;
        }

        .loa-field label {
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .08em;
        }

        .loa-input {
          height: 40px;
          border: 1px solid #cbd5e1;
          border-radius: 11px;
          padding: 0 10px;
          font-size: 12px;
          font-weight: 800;
          background: white;
          color: #071f45;
          outline: none;
        }

        .loa-table-card {
          background: white;
          border: 1.5px solid #111827;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 8px 22px rgba(15,23,42,.06);
        }

        .loa-table-head {
          padding: 15px 18px;
          background: #f8fafc;
          border-bottom: 1.5px solid #111827;
          display: flex;
          justify-content: space-between;
          gap: 14px;
          align-items: center;
        }

        .loa-table-head h3 {
          margin: 0;
          color: #071f45;
          font-size: 17px;
          font-weight: 950;
        }

        .loa-table-head p {
          margin: 4px 0 0;
          color: #64748b;
          font-size: 12px;
          font-weight: 750;
        }

        .loa-table-wrap {
          overflow-x: auto;
        }

        .loa-table {
          width: 100%;
          min-width: 1780px;
          border-collapse: collapse;
          font-size: 12px;
        }

        .loa-table th {
          background: #e2e8f0;
          color: #071f45;
          text-align: left;
          padding: 12px;
          border: 1.5px solid #111827;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: .08em;
          font-weight: 950;
          white-space: nowrap;
        }

        .loa-table td {
          padding: 12px;
          border: 1.5px solid #111827;
          vertical-align: top;
          background: white;
          color: #0f172a;
          font-weight: 750;
        }

        .loa-table tbody tr:nth-child(even) td {
          background: #f8fafc;
        }

        .loa-table tbody tr:hover td {
          background: #eef6ff;
        }

        .loa-title {
          color: #071f45;
          font-weight: 950;
        }

        .muted {
          color: #64748b;
          font-size: 11px;
          line-height: 1.6;
          font-weight: 750;
        }

        .loa-badge {
          border-radius: 999px;
          padding: 6px 10px;
          font-size: 10px;
          font-weight: 950;
          display: inline-block;
          white-space: nowrap;
        }

        .loa-badge.pending {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #f59e0b;
        }

        .loa-badge.accepted {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #16a34a;
        }

        .loa-badge.rejected {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #ef4444;
        }

        .loa-badge.generated {
          background: #dbeafe;
          color: #1e40af;
          border: 1px solid #93c5fd;
        }

        .loa-btn {
          border: 0;
          border-radius: 10px;
          padding: 8px 11px;
          font-size: 11px;
          font-weight: 950;
          cursor: pointer;
          text-decoration: none;
          text-align: center;
          display: inline-block;
        }

        .loa-btn.primary {
          background: #062b55;
          color: white;
        }

        .loa-btn.green {
          background: #16a34a;
          color: white;
        }

        .loa-btn.red {
          background: #ef4444;
          color: white;
        }

        .loa-btn.amber {
          background: #f59e0b;
          color: white;
        }

        .loa-btn.blue {
          background: #2563eb;
          color: white;
        }

        .loa-btn.gray {
          background: #e2e8f0;
          color: #071f45;
          border: 1px solid #cbd5e1;
        }

        .loa-btn:disabled {
          opacity: .45;
          cursor: not-allowed;
        }

        .loa-row-actions {
          display: grid;
          gap: 7px;
        }

        .date-input,
        .duration-input,
        .remark-input {
          width: 100%;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          padding: 8px;
          font-size: 12px;
          font-weight: 750;
          outline: none;
        }

        .remark-input {
          min-height: 68px;
          resize: vertical;
        }

        .count-green {
          color: #166534;
        }

        .count-amber {
          color: #b45309;
        }

        .count-red {
          color: #dc2626;
        }

        .count-muted {
          color: #64748b;
        }

        .empty {
          padding: 35px;
          text-align: center;
          color: #64748b;
          font-weight: 850;
        }

        @media(max-width:1300px) {
          .loa-stats {
            grid-template-columns: repeat(3, 1fr);
          }

          .loa-filter {
            grid-template-columns: 1fr;
          }
        }

        @media(max-width:700px) {
          .loa-stats {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <section className="loa-hero">
        <small>LOA / Work Order Control Room</small>
        <h2>Contractor Signed LOA Verification</h2>
        <p>
          Auto-generate LOA, manual upload, work countdown, signed LOA tracking
          and PMC accept/reject verification. NAFED mode shows only allocated
          state/district scoped awarded projects.
        </p>
      </section>

      <section className="loa-stats">
        <StatCard label="Awarded Projects" value={summary.total} note="Tender awarded" />
        <StatCard label="LOA Available" value={summary.loaAvailable} note="Generated / uploaded" />
        <StatCard label="Signed Submitted" value={summary.signedSubmitted} note="Contractor uploaded" />
        <StatCard label="Pending Review" value={summary.pending} note="PMC action required" />
        <StatCard label="Accepted" value={summary.accepted} note="Ready execution" />
        <StatCard label="Rejected" value={summary.rejected} note="Correction required" />
      </section>

      <section className="loa-filter">
        <div className="loa-field">
          <label>Status Filter</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="loa-input"
          >
            <option value="all">All LOA Records</option>
            <option value="loaMissing">LOA PDF Missing</option>
            <option value="signedPending">Signed LOA Pending</option>
            <option value="reviewPending">PMC Review Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="loa-field">
          <label>Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search project, PACS, contractor, district, tender ID..."
            className="loa-input"
          />
        </div>
      </section>

      <section className="loa-table-card">
        <div className="loa-table-head">
          <div>
            <h3>LOA / Work Order Register</h3>
            <p>Professional black-line table with generated, signed and PMC verified LOA details.</p>
          </div>
          <div className="muted">
            Records: <b>{filteredRows.length}</b>
          </div>
        </div>

        {loading ? (
          <div className="empty">Loading LOA register...</div>
        ) : filteredRows.length === 0 ? (
          <div className="empty">No LOA record found.</div>
        ) : (
          <div className="loa-table-wrap">
            <table className="loa-table">
              <thead>
                <tr>
                  <th>Sr</th>
                  <th>Project</th>
                  <th>PACS / Society</th>
                  <th>Contractor</th>
                  <th>Award Amount</th>
                  <th>Generate Date</th>
                  <th>Duration</th>
                  <th>Days Left</th>
                  <th>LOA PDF</th>
                  <th>Signed LOA</th>
                  <th>PMC Status</th>
                  <th>PMC Remarks</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredRows.map((r, idx) => {
                  const countdown = getCountdown({
                    ...r,
                    workStartDate: generateDateMap[r.id] || r.workStartDate,
                    workDurationDays: durationMap[r.id] || r.workDurationDays,
                  });

                  return (
                    <tr key={r.id}>
                      <td style={{ textAlign: "center", fontWeight: 950 }}>
                        {idx + 1}
                      </td>

                      <td>
                        <div className="loa-title">{r.projectTitle}</div>
                        <div className="muted">Tender ID: {r.id}</div>
                        <div className="muted">
                          {r.state || "—"} / {r.district || "—"}
                        </div>
                        <div className="muted">
                          Capacity: {r.capacityMt || r.projectCapacity || "—"} MT
                        </div>
                      </td>

                      <td>
                        <div className="loa-title">{r.pacsName}</div>
                        <div className="muted">{r.village || ""}</div>
                      </td>

                      <td>
                        <div className="loa-title">{r.contractorName}</div>
                        <div className="muted">UID: {r.contractorUid}</div>
                      </td>

                      <td style={{ textAlign: "right", fontWeight: 950 }}>
                        {formatINR(r.awardAmount)}
                      </td>

                      <td>
                        <input
                          type="date"
                          value={generateDateMap[r.id] || r.loaGenerateDate || ""}
                          onChange={(e) =>
                            setGenerateDateMap((p) => ({
                              ...p,
                              [r.id]: e.target.value,
                            }))
                          }
                          className="date-input"
                        />
                      </td>

                      <td>
                        <input
                          type="number"
                          placeholder="Days"
                          value={durationMap[r.id] || r.workDurationDays || ""}
                          onChange={(e) =>
                            setDurationMap((p) => ({
                              ...p,
                              [r.id]: e.target.value,
                            }))
                          }
                          className="duration-input"
                        />
                      </td>

                      <td style={{ textAlign: "center", fontWeight: 950 }}>
                        <div className={`count-${countdown.color}`}>
                          {countdown.daysLeft === "—"
                            ? "—"
                            : `${countdown.daysLeft} Days`}
                          <div className="muted">{countdown.label}</div>
                        </div>
                      </td>

                      <td>
                        <div className="loa-row-actions">
                          <button
                            type="button"
                            disabled={generatingId === r.id}
                            onClick={() => generateAutoLOA(r)}
                            className="loa-btn green"
                          >
                            {generatingId === r.id
                              ? "Generating..."
                              : r.loaPdfUrl
                              ? "Regenerate LOA"
                              : "Generate LOA"}
                          </button>

                          {r.loaPdfUrl && (
                            <a
                              href={r.loaPdfUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="loa-btn primary"
                            >
                              View LOA
                            </a>
                          )}

                          <label className="loa-btn amber">
                            {uploadingId === r.id ? "Uploading..." : "Manual Upload"}
                            <input
                              type="file"
                              accept="application/pdf"
                              style={{ display: "none" }}
                              disabled={uploadingId === r.id}
                              onChange={(e) =>
                                uploadLoaPdf(r, e.target.files?.[0])
                              }
                            />
                          </label>
                        </div>
                      </td>

                      <td style={{ textAlign: "center" }}>
                        {r.signedLoaUrl ? (
                          <a
                            href={r.signedLoaUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="loa-btn green"
                          >
                            View Signed
                          </a>
                        ) : (
                          <span style={{ color: "#b45309", fontWeight: 950 }}>
                            Pending
                          </span>
                        )}
                      </td>

                      <td style={{ textAlign: "center" }}>
                        <Badge status={r.loaPmcStatus || r.loaStatus || "PENDING"} />
                      </td>

                      <td>
                        <textarea
                          value={remarksMap[r.id] ?? r.loaPmcRemarks ?? ""}
                          onChange={(e) =>
                            setRemarksMap((p) => ({
                              ...p,
                              [r.id]: e.target.value,
                            }))
                          }
                          placeholder="PMC remarks..."
                          className="remark-input"
                        />
                      </td>

                      <td>
                        <div className="loa-row-actions">
                          <button
                            type="button"
                            disabled={editingId === r.id}
                            onClick={() => updateWorkSchedule(r)}
                            className="loa-btn blue"
                          >
                            {editingId === r.id ? "Saving..." : "Save Schedule"}
                          </button>

                          <button
                            type="button"
                            disabled={!r.signedLoaUrl || savingId === r.id}
                            onClick={() => setDecision(r, "ACCEPTED")}
                            className="loa-btn green"
                          >
                            Accept
                          </button>

                          <button
                            type="button"
                            disabled={!r.signedLoaUrl || savingId === r.id}
                            onClick={() => setDecision(r, "REJECTED")}
                            className="loa-btn red"
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}